import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardBody,
  Col,
  Row,
  Form,
  Label,
  Input,
  Spinner,
  InputGroup,
  FormGroup,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { get, postForm } from '../../../hook/request';
import { Btn, H3 } from '../../../AbstractElements';



type Theme = {
  primary: string;
  secondary: string;
};

export interface AppConfig {
  title: string;
  theme: Theme;
  images: {
    logo: string | File;    // string = URL dal backend, File = appena scelto
    favicon: string | File; // idem
  };
}

interface PanelDataFromApi {
  ID: number;
  nome: string;
  colore_primario: string;
  colore_secondario: string;
  logo: string;    // URL o path lato backend
  favicon: string; // URL o path lato backend
  [key: string]: any;
}

interface PanelDataResponse {
  status: string; 
  data: PanelDataFromApi;
}


interface SavePanelField {
  logo: string;
  favicon: string;
  id: number;
  colore_primario: string;
  colore_secondario: string;
  url_front: string;
}

interface SavePanelResponse {
  status: string;     
  field: SavePanelField;
}



const defaultConfig: AppConfig = {
  title: '',
  theme: {
    primary: '#308e87',
    secondary: '#f39159',
  },
  images: {
    logo: '',
    favicon: '',
  },
};



const ConfigThemeForm: React.FC = () => {
  const [form, setForm] = useState<AppConfig>(defaultConfig);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  const canSubmit = useMemo(() => {
    const hasTitle = form.title.trim() !== '';
    const hasPrimary = form.theme.primary.trim() !== '';
    const hasSecondary = form.theme.secondary.trim() !== '';

    const hasLogo =
      form.images.logo instanceof File
        ? true
        : (form.images.logo as string).trim() !== '';

    const hasFavicon =
      form.images.favicon instanceof File
        ? true
        : (form.images.favicon as string).trim() !== '';

    return hasTitle && hasPrimary && hasSecondary && hasLogo && hasFavicon;
  }, [form]);

  const fetchConfig = async () => {
    try {
      const res = await get<PanelDataResponse>('/panel/data');

      if (res && res.data && res.data.status === 'ok') {
        const apiData = res.data.data;

        const mapped: AppConfig = {
          title: apiData.nome,
          theme: {
            primary: apiData.colore_primario,
            secondary: apiData.colore_secondario,
          },
          images: {
            logo: apiData.logo,       // URL string
            favicon: apiData.favicon, // URL string
          },
        };

        setForm(mapped);
      }
    } catch (err) {
      console.error(err);
      toast.error('Errore nel caricamento della configurazione');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);


  const onChangeRoot = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const onChangeTheme = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      theme: {
        ...prev.theme,
        [name]: value,
      },
    }));
  };


  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: 'logo' | 'favicon'
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setForm(prev => ({
      ...prev,
      images: {
        ...prev.images,
        [field]: file,
      },
    }));
  };



  const onSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  if (!canSubmit) return;

  setSaving(true);
  try {
    const fd = new FormData();

    fd.append('nome', form.title);
    fd.append('colore_primario', form.theme.primary);
    fd.append('colore_secondario', form.theme.secondary);

    if (form.images.logo instanceof File) {
      fd.append('logo', form.images.logo);
    }

    if (form.images.favicon instanceof File) {
      fd.append('favicon', form.images.favicon);
    }

    const res = await postForm<SavePanelResponse>('/panel/data', fd);

    toast.success('Configurazione salvata con successo');

    const {
      url_front,
      logo,
      favicon,
      colore_primario,
      colore_secondario,
    } = res.data.field;

    if (url_front) {

      const baseUrl = url_front.replace(/\/$/, ''); 
      const frontFd = new FormData();
      frontFd.append('nome', form.title);
      frontFd.append('colore_primario', colore_primario);
      frontFd.append('colore_secondario', colore_secondario);
      frontFd.append('logo', logo);       
      frontFd.append('favicon', favicon); 

      try {
        const frontResp = await fetch(`${baseUrl}/config.php`, {
          method: 'POST',
          body: frontFd,
        });

        if (!frontResp.ok) {
          throw new Error(`HTTP error ${frontResp.status}`);
        }

        toast.success('File config.json aggiornato sul front');
      } catch (err) {
        console.error('Errore chiamando config.php', err);
        toast.error(
          'Configurazione salvata nel backend, ma errore nella generazione del file sul front.'
        );
      }
    }
    fetchConfig();
  } catch (err) {
    console.error(err);
    toast.error('Errore nel salvataggio della configurazione');
  } finally {
    setSaving(false);
  }
};



  if (loading) {
    return (
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody className="d-flex align-items-center gap-2">
            <Spinner size="sm" />
            <span>Caricamento configurazione…</span>
          </CardBody>
        </Card>
      </Col>
    );
  }

  // Helpers per le preview
  const getImageSrc = (value: string | File) => {
    if (value instanceof File) {
      return URL.createObjectURL(value);
    }
    return value;
  };

  return (
    <div className="page-body pt-4">
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody>
            <Form onSubmit={onSubmit}>
              <div className="d-flex justify-content-between align-items-center">
                <H3 className="mb-3">Configurazione Tema</H3>
                <Link to="/configurazione">
                  <Btn className="px-4" color="primary">
                    Torna Indietro
                  </Btn>
                </Link>
              </div>

              {/* Generale */}
              <hr />
              <h4 className="mt-3 mb-2">Generale</h4>
              <Row className="g-3">
                <Col lg={6}>
                  <FormGroup>
                    <Label>Titolo progetto</Label>
                    <Input
                      name="title"
                      placeholder="Titolo del progetto"
                      value={form.title}
                      onChange={onChangeRoot}
                    />
                  </FormGroup>
                </Col>
              </Row>

              {/* Colori */}
              <hr />
              <h4 className="mt-4 mb-2">Colori</h4>
              <Row className="g-3">
                <Col lg={6}>
                  <FormGroup>
                    <Label>Colore primario</Label>
                    <InputGroup className='d-flex flex-row gap-2'>
                      <div>
                        <Input
                          style={{ width: 100 }}
                          type="color"
                          name="primary"
                          value={form.theme.primary}
                          onChange={onChangeTheme}
                        />
                      </div>
                      <div>
                        <Input
                          style={{ width: 200 }}
                          name="primary"
                          value={form.theme.primary}
                          onChange={onChangeTheme}
                          placeholder="#308e87"
                        />
                      </div>
                    </InputGroup>
                  </FormGroup>
                </Col>
                <Col lg={6}>
                  <FormGroup>
                    <Label>Colore secondario</Label>
                    <InputGroup className='d-flex flex-row gap-2'>
                      <div>
                        <Input
                          style={{ width: 100 }}
                          type="color"
                          name="secondary"
                          value={form.theme.secondary}
                          onChange={onChangeTheme}
                        />
                      </div>
                      <div>
                        <Input
                          style={{ width: 200 }}
                          name="secondary"
                          value={form.theme.secondary}
                          onChange={onChangeTheme}
                          placeholder="#f39159"
                        />
                      </div>
                    </InputGroup>
                  </FormGroup>
                </Col>
              </Row>

              {/* Immagini */}
              <hr />
              <h4 className="mt-4 mb-2">Immagini</h4>
              <Row className="g-3">
                <Col lg={6}>
                  <FormGroup>
                    <Label>Logo</Label>
                    <Input
                      id="formFileLogo"
                      type="file"
                      accept="image/*"
                      onChange={e => handleFileChange(e, 'logo')}
                    />
                  </FormGroup>
                </Col>
                <Col lg={6}>
                  <FormGroup>
                    <Label>Favicon / Immagine frontespizio</Label>
                    <Input
                      id="formFileFavicon"
                      type="file"
                      accept="image/*"
                      onChange={e => handleFileChange(e, 'favicon')}
                    />
                  </FormGroup>
                </Col>
              </Row>

              {/* Preview */}
              <Row className="g-3 mt-3">
                <Col lg={6}>
                  <Label>Preview Logo</Label>
                  {form.images.logo && (
                    <div className="border rounded p-2 d-flex justify-content-center">
                      <img
                        src={getImageSrc(form.images.logo)}
                        alt="Preview logo"
                        style={{ maxWidth: '100%', height: 140, objectFit: 'contain' }}
                      />
                    </div>
                  )}
                </Col>
                <Col lg={6}>
                  <Label>Preview Favicon / Frontespizio</Label>
                  {form.images.favicon && (
                    <div className="border rounded p-2 d-flex justify-content-center">
                      <img
                        src={getImageSrc(form.images.favicon)}
                        alt="Preview favicon"
                        style={{ maxWidth: '100%', height: 140, objectFit: 'cover' }}
                      />
                    </div>
                  )}
                </Col>
              </Row>

              {/* Bottoni */}
              <Row className="g-3 mt-4">
                <Col xs={12} className="d-flex gap-2">
                  <Btn
                    color="primary"
                    className="px-4"
                    type="submit"
                    disabled={!canSubmit || saving}
                  >
                    {saving ? <Spinner size="sm" /> : 'Salva'}
                  </Btn>
                  <Link to="/configurazione">
                    <Btn color="danger" className="px-4" type="button">
                      Annulla
                    </Btn>
                  </Link>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
      </Col>
    </div>
  );
};

export default ConfigThemeForm;
